/**
 * Skills hooks — the nurse's own fold and the records behind it.
 *
 * Replaces useKnowledgeProfile, which read the retired `/me/expertise`
 * surface and derived a three-view dashboard (draft / published /
 * changes-since-publish) client-side. The new model has no
 * changes-since-publish state to derive: a skill has a `state` and a
 * `visibility`, and the server's fold is the whole answer.
 */

import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@frontend/api/client';
import {
  declareOwnSkill,
  dropUserSkillTarget,
  listOwnSkills,
  listSkills,
  listUserSkillEndorsements,
  listUserSkillEvents,
  listUserSkills,
  moveOwnSkill,
  setUserSkillTarget,
  type DeclareSkillRequest,
  type HeldSkill,
  type HeldSkillPage,
  type HeldSkills,
  type OwnSkillVerb,
  type SetTargetRequest,
  type Target,
  type TargetState,
} from '@frontend/api/endpoints/skills';
import { heldSkillTitle } from '@frontend/lib/skillTitle';
import { compareIsoDesc } from '@frontend/utils/dates';
import { toast } from '@frontend/hooks/use-toast';
import { useAuthenticatedQuery } from '@frontend/hooks/useAuthenticatedQuery';
import { useAuthStore } from '@frontend/stores/auth';

/**
 * Exported because a completed quiz run moves the fold from outside this file:
 * an attempt can achieve a target, and the board that shows it is a different
 * screen from the one the run finished on.
 */
export const OWN_SKILLS_KEY = ['skills', 'me'] as const;
const userSkillsKey = (userId: string) => ['skills', 'user', userId] as const;
const endorsementsKey = (userId: string, skillKey: string) =>
  ['skills', 'endorsements', userId, skillKey] as const;
const timelineKey = (userId: string, skillKey: string) =>
  ['skills', 'events', userId, skillKey] as const;

/**
 * The tenant skill catalogue.
 *
 * Knowledge, not a fold: a file in the customer's repository, versioned and
 * read at runtime. It changes only when that file changes, so it is cached for
 * the session rather than refetched per mount.
 */
export function useSkillCatalogue() {
  return useAuthenticatedQuery({
    queryKey: ['skills', 'catalogue'] as const,
    queryFn: () => listSkills(),
    staleTime: Infinity,
  });
}

/** Your own fold: every skill on your stream, private and published alike. */
export function useOwnSkills() {
  return useAuthenticatedQuery<HeldSkillPage>({
    queryKey: OWN_SKILLS_KEY,
    queryFn: () => listOwnSkills(),
  });
}

/**
 * The endorsements a colleague wrote on one of your skills.
 *
 * There is no `/me/skills/{skill}/endorsements`: the subject reads their own
 * received endorsements through the colleague route, where they are the
 * subject. `enabled` is how a caller says "only when this row is open" — the
 * records are read on demand rather than N+1 across the whole fold.
 */
export function useSkillEndorsements(skillKey: string, enabled: boolean) {
  const userId = useAuthStore((s) => s.user?.id);
  return useAuthenticatedQuery({
    queryKey: endorsementsKey(userId ?? '', skillKey),
    queryFn: () => listUserSkillEndorsements(userId!, skillKey),
    enabled: enabled && !!userId,
  });
}

/**
 * The records behind one skill, oldest first.
 *
 * The proposal card needs it: `reasoning` is the curator's account of why it
 * raised the skill, and the timeline is the only route that carries it. A
 * proposal accepted without that on screen is the curator's words in the
 * nurse's mouth, which is the thing adr/0025 removed the promote flow to stop.
 */
export function useSkillTimeline(skillKey: string, enabled: boolean) {
  const userId = useAuthStore((s) => s.user?.id);
  return useAuthenticatedQuery({
    queryKey: timelineKey(userId ?? '', skillKey),
    queryFn: () => listUserSkillEvents(userId!, skillKey),
    enabled: enabled && !!userId,
  });
}

export interface DeclareVars {
  request: DeclareSkillRequest;
  idempotencyKey: string;
}

export interface MoveVars {
  skillKey: string;
  verb: OwnSkillVerb;
  /** The fold's version for this skill, sent as `If-Match`. */
  version: string;
  idempotencyKey: string;
}

export interface UseOwnSkillMutationsResult {
  declare: ReturnType<typeof useMutation<unknown, Error, DeclareVars>>;
  move: ReturnType<typeof useMutation<HeldSkill, Error, MoveVars>>;
}

/**
 * The writes on your own fold.
 *
 * Both mint their `Idempotency-Key` at the call site and hold it in
 * `variables`, so it stays stable across TanStack's automatic retries of the
 * same attempt and a retried append records the fact once.
 *
 * A 412 gets its own answer. The verb was decided against a version that has
 * since moved, so the state on screen is not the state the decision applied to.
 * We refetch the fold and say so, which is what leaves the nurse looking at the
 * CURRENT state instead of a toggle that silently stayed where she left it.
 */
export function useOwnSkillMutations(): UseOwnSkillMutationsResult {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: OWN_SKILLS_KEY });

  const onWriteError = (error: Error) => {
    if (error instanceof ApiError && error.status === 412) {
      // Refetch rather than invalidate: invalidate leaves a cached page on
      // screen until something remounts, which is exactly the stale toggle.
      void qc.refetchQueries({ queryKey: OWN_SKILLS_KEY });
      toast({
        variant: 'destructive',
        title: t('skills.toast.staleVersion'),
        description: t('skills.toast.staleVersionDesc'),
      });
      return;
    }
    toast({
      variant: 'destructive',
      title: t('skills.toast.saveFailed'),
      description: t('skills.toast.saveFailedDesc'),
    });
  };

  const declare = useMutation<unknown, Error, DeclareVars>({
    mutationFn: (v) => declareOwnSkill(v.request, v.idempotencyKey),
    onSuccess: invalidate,
    onError: onWriteError,
  });

  const move = useMutation<HeldSkill, Error, MoveVars>({
    mutationFn: (v) => moveOwnSkill(v.skillKey, v.verb, v.version, v.idempotencyKey),
    onSuccess: (_result, v) => {
      invalidate();
      // Accepting a proposal writes a declaration, so the timeline behind that
      // skill has a new record the card may be showing.
      void qc.invalidateQueries({ queryKey: ['skills', 'events'] });
      if (v.verb === 'accept') void qc.invalidateQueries({ queryKey: ['skills', 'endorsements'] });
    },
    onError: onWriteError,
  });

  return { declare, move };
}

// ---------------------------------------------------------------------------
// What a practitioner is working toward
// ---------------------------------------------------------------------------

/**
 * One target with the name of the skill it names.
 *
 * A target has no title of its own — it names exactly one skill and a day. The
 * two travel together because every surface that shows a target shows the
 * skill, and pairing them at the fold is what stops each board re-deriving the
 * name its own way.
 */
export interface SkillTarget {
  target: Target;
  skillTitle: string;
}

/**
 * The targets on a fold, in the order the fold returned the skills.
 *
 * `catalogueTitle` is the reader's own lookup from a catalogue skill id to the
 * name to show, built with `catalogueTitleLookup`. A caller that has not loaded
 * the catalogue may omit it and every row falls back to the skill key, which is
 * how the board read before and is why a nurse saw `advanced-life-support`
 * where `Erweiterte Reanimation` belongs: the fold names a catalogue skill by
 * id and carries no title, because a title has a locale and the reader picks
 * it.
 */
export function flattenTargets(
  items: HeldSkill[],
  catalogueTitle?: (skillId: string) => string | undefined,
): SkillTarget[] {
  return items.flatMap((held) =>
    held.target === null
      ? []
      : [{ target: held.target, skillTitle: heldSkillTitle(held, catalogueTitle) }],
  );
}

/** Open work above the record of work already closed. */
const STATE_ORDER: Record<TargetState, number> = { open: 0, achieved: 1, dropped: 2 };

/**
 * Still-open targets first, then soonest by exam date where there is one and by
 * due date otherwise, then newest.
 *
 * State leads because a target that is reached or dropped is a record, not
 * work, and its day is usually in the past — sorting on the day alone would
 * push last month's achievement above next week's obligation.
 *
 * A target always carries a `due_date`, so there is no undated bucket to sink;
 * `exam_date` is the sharper deadline when it is set, because the exam is the
 * event the due date exists for. Both are civil dates in ISO order, so a
 * codepoint comparison IS a chronological one, and no clock is read.
 */
export function sortTargets(a: SkillTarget, b: SkillTarget): number {
  const stateA = STATE_ORDER[a.target.state];
  const stateB = STATE_ORDER[b.target.state];
  if (stateA !== stateB) return stateA - stateB;
  const dateA = a.target.exam_date ?? a.target.due_date;
  const dateB = b.target.exam_date ?? b.target.due_date;
  if (dateA !== dateB) return dateA < dateB ? -1 : 1;
  return compareIsoDesc(a.target.created, b.target.created);
}

/**
 * A target the trainee is late on: its day has passed AND it is still open.
 *
 * `state` is the ONLY answer to whether a target is reached, and it is why the
 * `state === 'open'` half is not redundant. A target achieved last month still
 * has a due date in the past, and calling that overdue would chase a trainee
 * for work she has already done. `today` is the caller's civil date
 * (`todayCivilDate`), compared as a DAY: a due date is a day a nurse named, not
 * an instant, so no timezone arithmetic belongs here.
 */
export function isOverdue(target: Target, today: string): boolean {
  return target.state === 'open' && target.due_date < today;
}

/**
 * Every roster member's targets, one read per member.
 *
 * There is no unit-scoped targets route: a target lives on its subject's own
 * `(user, skills)` stream, and `GET /users/{id}/skills` is how a lead reads one
 * trainee's. The fan-out is `useQueries`, the same shape useMyCurriculumPaths
 * uses for cohorts, so one trainee's read failing is visible as a failure of
 * the whole enrichment rather than as that trainee silently having no targets.
 */
export interface TraineeTargets {
  byUser: Map<string, SkillTarget[]>;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

export function useTraineeTargets(
  userIds: string[],
  catalogueTitle?: (skillId: string) => string | undefined,
): TraineeTargets {
  const queries = useQueries({
    queries: userIds.map((userId) => ({
      queryKey: userSkillsKey(userId),
      queryFn: (): Promise<HeldSkills> => listUserSkills(userId),
      refetchOnWindowFocus: true,
    })),
  });

  const byUser = new Map<string, SkillTarget[]>();
  userIds.forEach((userId, i) => {
    const data = queries[i]?.data;
    if (data) byUser.set(userId, flattenTargets(data.items, catalogueTitle));
  });

  return {
    byUser,
    isLoading: queries.some((q) => q.isLoading),
    isError: queries.some((q) => q.isError),
    refetch: () => queries.forEach((q) => void q.refetch()),
  };
}

export interface SetTargetVars {
  userId: string;
  skillKey: string;
  request: SetTargetRequest;
  idempotencyKey: string;
}

export interface DropTargetVars {
  userId: string;
  skillKey: string;
  targetId: string;
  /** The TARGET's own version, sent as `If-Match` — never the skill's. */
  version: string;
  idempotencyKey: string;
}

export interface UseTargetMutationsResult {
  set: ReturnType<typeof useMutation<Target, Error, SetTargetVars>>;
  drop: ReturnType<typeof useMutation<Target, Error, DropTargetVars>>;
}

/**
 * The two writes a lead makes on a trainee's targets.
 *
 * There is no third: a target is set and it is dropped. Whether it was reached
 * is the fold's answer, so there is nothing here that marks one achieved.
 *
 * Both mint their `Idempotency-Key` at the call site and hold it in
 * `variables`, so a retry of the same attempt appends the record once. A 412 on
 * the drop gets the same treatment as the fold verbs: refetch and say so, which
 * leaves the lead looking at the current state rather than a row that silently
 * stayed put.
 */
export function useTargetMutations(): UseTargetMutationsResult {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const onWriteError = (error: Error, vars: { userId: string }) => {
    if (error instanceof ApiError && error.status === 412) {
      void qc.refetchQueries({ queryKey: userSkillsKey(vars.userId) });
      toast({
        variant: 'destructive',
        title: t('skills.toast.staleVersion'),
        description: t('skills.toast.staleVersionDesc'),
      });
      return;
    }
    toast({
      variant: 'destructive',
      title: t('skills.toast.saveFailed'),
      description: t('skills.toast.saveFailedDesc'),
    });
  };

  const invalidate = (_result: Target, vars: { userId: string }) => {
    void qc.invalidateQueries({ queryKey: userSkillsKey(vars.userId) });
    // The subject reads the same stream through `/me/skills`, and on a lead
    // setting a target on herself that IS the board beside this one.
    void qc.invalidateQueries({ queryKey: OWN_SKILLS_KEY });
  };

  const set = useMutation<Target, Error, SetTargetVars>({
    mutationFn: (v) => setUserSkillTarget(v.userId, v.skillKey, v.request, v.idempotencyKey),
    onSuccess: invalidate,
    onError: onWriteError,
  });

  const drop = useMutation<Target, Error, DropTargetVars>({
    mutationFn: (v) =>
      dropUserSkillTarget(v.userId, v.skillKey, v.targetId, v.version, v.idempotencyKey),
    onSuccess: invalidate,
    onError: onWriteError,
  });

  return { set, drop };
}
